import { HttpClient } from './HttpClient.js';
import { SessionStorage } from './SessionStorage.js';
import { toUserOnboardRequest } from './dto/UserData.js';

/**
 * AuthService - Handles all authentication operations
 * No inheritance, clean separation of concerns
 */
export class AuthService {
  constructor() {
    this.httpClient = new HttpClient();
    this.sessionStorage = new SessionStorage();
    
    // Environment configuration
    this.authBaseUrl = process.env.REACT_APP_IDENTITY_SERVER_URL;
    
    console.log('AuthService initialized with authBaseUrl:', this.authBaseUrl);
  }

  /**
   * Fetch current user data from server
   * @returns {Promise<Object|null>} User data or null if not authenticated
   */
  async fetchCurrentUser() {
    console.log('Fetching current user from server');
    
    try {
      const response = await this.httpClient.get(`${this.authBaseUrl}/auth/user/fetch`);
      
      if (response.status === 401 || response.status === 403) {
        console.log('User not authenticated on server');
        return null;
      }

      if (!response.ok) {
        console.warn('Failed to fetch current user:', response.status);
        return null;
      }

      const userData = await this.httpClient.parseJson(response);
      console.log('Successfully fetched current user data');
      
      this.sessionStorage.setUserData(userData);
      
      return this.withTokenRefresh(userData);
    } catch (error) {
      console.error('Error fetching current user:', error);
      return null;
    }
  }

  /**
   * Fetch current user data AND refresh token
   * @returns {Promise<Object|null>} User data or null if not authenticated
   */
  async fetchCurrentUserWithToken() {
    console.log('Fetching current user with token refresh');
    
    try {
      const response = await this.httpClient.get(`${this.authBaseUrl}/auth/user/token/fetch`);
      
      if (response.status === 401 || response.status === 403) {
        console.log('User not authenticated for token refresh');
        return null;
      }

      if (!response.ok) {
        console.warn('Failed to refresh token:', response.status);
        return null;
      }

      const userData = await this.httpClient.parseJson(response);
      console.log('Successfully refreshed token and fetched user data');
      
      this.sessionStorage.setUserData(userData);
      
      return this.withTokenRefresh(userData);
    } catch (error) {
      console.error('Error refreshing token:', error);
      return null;
    }
  }

  /**
   * Login user (initiates 2FA process)
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Object with email and requiresVerification flag
   */
  async login(email, password) {
    console.log('Logging in user:', email);
    
    const response = await this.httpClient.post(`${this.authBaseUrl}/auth/login`, {
      email,
      password
    });

    this.httpClient.checkResponse(response);

    const data = await this.httpClient.parseJson(response);
    console.log('Initial login successful, verification required');
    
    return {
      email: data.email,
      requiresVerification: true
    };
  }

  /**
   * NHS Login
   * @param {string} code - NHS login authorization code
   * @returns {Promise<Object>} User data
   */
  async nhsLogin(code) {
    console.log('Performing NHS login');
    
    const response = await this.httpClient.post(`${this.authBaseUrl}/auth/nhs-login`, {
      code
    });

    this.httpClient.checkResponse(response);

    const userData = await this.httpClient.parseJson(response);
    console.log('NHS login successful');

    this.sessionStorage.setUserData(userData);

    return this.withTokenRefresh(userData);
  }

  /**
   * Verify 2FA code
   * @param {string} email - User email
   * @param {string} verificationCode - 2FA verification code
   * @returns {Promise<Object>} User data
   */
  async verify2FA(email, verificationCode) {
    console.log('Verifying 2FA code for user:', email);
    
    const response = await this.httpClient.post(`${this.authBaseUrl}/auth/verify-2fa`, {
      email,
      verificationCode
    });

    this.httpClient.checkResponse(response);

    const userData = await this.httpClient.parseJson(response);
    console.log('2FA verification successful');
    
    this.sessionStorage.setUserData(userData);

    return this.withTokenRefresh(userData);
  }

  /**
   * Sign up a new user with invitation code
   * @param {string} email - User email
   * @param {string} signUpVerificationCode - Invitation code
   * @param {string} password - User password
   * @returns {Promise<Object>} Object with email and requiresVerification flag
   */
  async signup(email, signUpVerificationCode, password) {
    console.log('Signing up user:', email);
    
    const response = await this.httpClient.post(`${this.authBaseUrl}/auth/signup`, {
      email,
      signUpVerificationCode,
      password
    });

    this.httpClient.checkResponse(response);

    const data = await this.httpClient.parseJson(response);
    console.log('Initial signup successful, verification required');

    return {
      email: data.email,
      requiresVerification: true
    };
  }

  /**
   * Logout user
   */
  async logout() {
    console.log('Logging out user');
    
    try {
      await this.httpClient.post(`${this.authBaseUrl}/auth/logout`);
      console.log('Server logout successful');
    } catch (error) {
      console.error('Error during server logout:', error);
      // Continue with local cleanup even if server logout fails
    } finally {
      // Clear local session storage
      this.sessionStorage.clearUserData();
    }
  }

  /**
   * Onboard a new user
   * @param {string} email - User email
   * @param {string} name - User name
   * @param {string} role - User role
   * @param {Array<string>} clinicIds - Clinic IDs to onboard the user into
   * @returns {Promise<Object>} Response data
   */
  async onboardUser(email, name, role, clinicIds) {
    const normalizedEmail = email.trim().toLowerCase();
    console.log('Onboarding new user:', normalizedEmail);
    
    const response = await this.httpClient.post(
      `${this.authBaseUrl}/api/clinic/user/onboard`,
      toUserOnboardRequest({
        email: normalizedEmail,
        name,
        role,
        clinicIds,
      })
    );

    this.httpClient.checkResponse(response);

    const data = await this.httpClient.parseJson(response);
    console.log('User onboarding successful');
    
    return data;
  }

  /**
   * NHS Onboard - link NHS user ID to existing user
   * @param {string} email - User email
   * @param {string} code - NHS authorization code
   * @returns {Promise<Object>} Updated user data
   */
  async nhsOnboard(email, code) {
    console.log('Starting NHS onboarding for user:', email);
    
    const response = await this.httpClient.post(`${this.authBaseUrl}/api/clinic/user/nhs-onboard`, {
      email,
      code
    });

    this.httpClient.checkResponse(response);

    const data = await this.httpClient.parseJson(response);
    console.log('NHS onboard successful');

    // Get current user data and update with NHS user ID
    const currentUser = this.sessionStorage.getUserData() || {};
    const nhsUserId = data.nhsUserId || data.userId;
    
    if (!nhsUserId) {
      throw new Error('NHS onboard response missing nhsUserId/userId');
    }

    const updatedUserData = {
      ...currentUser,
      nhsUserId: nhsUserId
    };
    
    this.sessionStorage.setUserData(updatedUserData);

    return updatedUserData;
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} True if authenticated
   */
  isAuthenticated() {
    return this.sessionStorage.isAuthenticated();
  }

  /**
   * Get current user data
   * @returns {Object|null} User data or null
   */
  getUserData() {
    return this.sessionStorage.getUserData();
  }

  /**
   * Set user data (for external updates)
   * @param {Object} userData - User data to store
   */
  setUserData(userData) {
    this.sessionStorage.setUserData(userData);
  }

  /**
   * Return the normalized (stored) user data with the server's lastTokenRefresh
   * timestamp attached. SessionStorage normalization strips unknown fields, so
   * we re-attach lastTokenRefresh (epoch millis) from the raw server response
   * for the SessionManager to use as its refresh clock. Not persisted.
   * @param {Object} rawUserData - Raw parsed server response
   * @returns {Object|null} Normalized user data with lastTokenRefresh, or null
   */
  withTokenRefresh(rawUserData) {
    const user = this.sessionStorage.getUserData();
    if (user && rawUserData && rawUserData.lastTokenRefresh != null) {
      user.lastTokenRefresh = rawUserData.lastTokenRefresh;
    }
    return user;
  }
}

